/**
 * RitualDetailAdminBar Component
 * Fixed bottom bar shown only to ritual creators (owners)
 * Based on Figma design - Frame 268 component
 */

import { ChevronsUpDown } from "lucide-react"
import { EyeIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTranslation } from "@/language"

export type RitualStatus = "active" | "completed" | "canceled" | "draft"

export type StatusChangeAction = "complete" | "cancel" | "publish"

interface RitualDetailAdminBarProps {
  status?: RitualStatus
  updatedAt?: string | null
  updatedBy?: { id: string; name: string | null; email: string | null } | null
  isViewingAsUser?: boolean
  isPublishing?: boolean
  onEdit?: () => void
  onViewAsUser?: () => void
  onStatusChange?: (action: StatusChangeAction) => void
}

/**
 * Format the "last updated" time for display
 */
function formatLastUpdated(timeAgo: string | null | undefined): string | null {
  if (!timeAgo) return null

  // Parse the date - handle ISO string format from backend
  const date = new Date(timeAgo)

  // Check if date is valid
  if (Number.isNaN(date.getTime())) {
    console.warn("Invalid date received:", timeAgo)
    return null
  }

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()

  // Handle future dates or very old dates
  if (diffMs < 0) {
    return "Just updated"
  }

  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  let timeText: string
  if (diffMins < 1) {
    timeText = "just now"
  } else if (diffMins < 60) {
    timeText = `${diffMins}m ago`
  } else if (diffHours < 24) {
    timeText = `${diffHours}h ago`
  } else if (diffDays < 7) {
    timeText = `${diffDays}d ago`
  } else {
    timeText = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
  }

  return `Last updated ${timeText}`
}

export function RitualDetailAdminBar({
  status = "active",
  updatedAt,
  updatedBy,
  isViewingAsUser = false,
  isPublishing = false,
  onEdit,
  onViewAsUser,
  onStatusChange,
}: RitualDetailAdminBarProps) {
  const { t } = useTranslation("rituals")
  // Format the last updated text
  const lastUpdatedText = formatLastUpdated(updatedAt ?? null)
  const userFirstName = updatedBy?.name?.split(" ")[0] || null

  // Build the full "last updated" display text
  const fullLastUpdatedText =
    lastUpdatedText && userFirstName
      ? `${lastUpdatedText} by ${userFirstName}`
      : lastUpdatedText
        ? lastUpdatedText
        : null

  // Status-specific styles
  const statusStyles = {
    active: {
      background: "#24392D",
      border: "1px solid #1A6538",
      dotColor: "#07AE49",
      textColor: "#07AE49",
      chevronColor: "#07AE49",
    },
    completed: {
      background: "#251D11",
      border: "1px solid #C9A876",
      dotColor: "#C9A876",
      textColor: "#C9A876",
      chevronColor: "#C9A876",
    },
    canceled: {
      background: "rgba(200, 103, 87, 0.05)",
      border: "1px solid #C86757",
      dotColor: "#C86757",
      textColor: "#C86757",
      chevronColor: "#C86757",
    },
    draft: {
      background: "rgba(242, 240, 235, 0.05)",
      border: "1px solid rgba(242, 240, 235, 0.25)",
      dotColor: "#F2F0EB",
      textColor: "#F2F0EB",
      chevronColor: "#F2F0EB",
    },
  }

  const currentStatus = statusStyles[status]
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1)

  // Show dropdown for transitionable states: "active" (complete/cancel) and "draft" (publish)
  const showStatusDropdown = status === "active" || status === "draft"

  return (
    <div
      className="lg:flex hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-50 items-center justify-between p-3 rounded-full w-[850px]"
      style={{
        backgroundColor: "rgba(66, 66, 66, 0.95)",
        border: "1px solid rgba(242, 240, 235, 0.15)",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      }}
    >
      {/* Left Section: Status Badge + Last Updated */}
      <div className="flex items-center gap-3">
        {/* Status Badge / Dropdown */}
        {showStatusDropdown ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 px-4 py-2 h-[40px] rounded-full transition-opacity hover:opacity-80"
                style={{
                  backgroundColor: currentStatus.background,
                  border: currentStatus.border,
                }}
              >
                {/* Status Dot */}
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{
                    backgroundColor: currentStatus.dotColor,
                  }}
                />
                {/* Status Text */}
                <span
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{
                    color: currentStatus.textColor,
                  }}
                >
                  {statusLabel}
                </span>
                <ChevronsUpDown className="h-4 w-4" color={currentStatus.chevronColor} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-48 bg-[#1a1a1a] border border-[#333333] rounded-xl overflow-hidden p-1.5 space-y-2"
            >
              {status === "draft" ? (
                <DropdownMenuItem
                  className="cursor-pointer rounded-full px-4 py-2.5 h-auto hover:opacity-90 transition-opacity"
                  style={{
                    backgroundColor: "#24392D",
                    border: "1px solid #1A6538",
                  }}
                  disabled={isPublishing}
                  onClick={() => onStatusChange?.("publish")}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#07AE49" }} />
                    <span
                      className="text-xs font-semibold uppercase tracking-wide"
                      style={{ color: "#07AE49" }}
                    >
                      {isPublishing ? t("adminBar.publishing") : t("adminBar.publishRitual")}
                    </span>
                  </div>
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem
                    className="cursor-pointer rounded-full px-4 py-2.5 h-auto hover:opacity-90 transition-opacity"
                    style={{
                      backgroundColor: "#251D11",
                      border: "1px solid #C9A876",
                    }}
                    onClick={() => onStatusChange?.("complete")}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: "#C9A876" }}
                      />
                      <span
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{ color: "#C9A876" }}
                      >
                        {t("adminBar.completeRitual")}
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer rounded-full px-4 py-2.5 h-auto hover:opacity-90 transition-opacity"
                    style={{
                      backgroundColor: "rgba(200, 103, 87, 0.05)",
                      border: "1px solid #C86757",
                    }}
                    onClick={() => onStatusChange?.("cancel")}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: "#C86757" }}
                      />
                      <span
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{ color: "#C86757" }}
                      >
                        {t("adminBar.cancelRitual")}
                      </span>
                    </div>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          // Static status badge for draft/completed/canceled
          <div
            className="flex items-center gap-2 px-4 py-2 h-[40px] rounded-full"
            style={{
              backgroundColor: currentStatus.background,
              border: currentStatus.border,
            }}
          >
            {/* Status Dot */}
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{
                backgroundColor: currentStatus.dotColor,
              }}
            />
            {/* Status Text */}
            <span
              className="text-xs font-semibold uppercase tracking-wide"
              style={{
                color: currentStatus.textColor,
              }}
            >
              {statusLabel}
            </span>
          </div>
        )}

        {/* Divider */}
        {fullLastUpdatedText && (
          <>
            <div
              className="hidden sm:block"
              style={{
                width: "1px",
                height: "16px",
                backgroundColor: "rgba(242, 240, 235, 0.15)",
              }}
            />
            {/* Last Updated Text */}
            <span className="hidden sm:block text-[14px] text-[#F2F0EB] opacity-50">
              {fullLastUpdatedText}
            </span>
          </>
        )}
      </div>

      {/* Right Section: Action Buttons */}
      <div className="flex items-center gap-2">
        {/* View as User/Admin Button - Filled */}
        <Button
          variant={"primary"}
          onClick={onViewAsUser}
          className="flex items-center gap-2 px-6 py-2 h-[40px] rounded-full text-xs font-semibold uppercase tracking-wide bg-white"
        >
          <EyeIcon className="!w-5 !h-5" color="#000000" />
          <span className="hidden sm:inline">
            {isViewingAsUser ? t("adminBar.viewAsAdmin") : t("adminBar.viewAsUser")}
          </span>
        </Button>

        {/* Edit Ritual Button - Outline */}
        <Button
          variant={"outline"}
          onClick={onEdit}
          className="flex items-center gap-2 px-8 py-2 h-[40px] rounded-full transition-colors text-xs font-semibold uppercase tracking-wide"
        >
          <span className="hidden sm:inline">{t("adminBar.editRitual")}</span>
        </Button>

        {/* More Options */}
        <button
          className="flex items-center justify-center w-10 h-10 rounded-full transition-colors"
          style={{ color: "rgba(242, 240, 235, 0.5)" }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="1" />
            <circle cx="19" cy="12" r="1" />
            <circle cx="5" cy="12" r="1" />
          </svg>
        </button>
      </div>
    </div>
  )
}
